import { ComplexTable } from '@synergy/table';
import { Col, Row } from 'antd';
import clsx from 'clsx';
import i18next from "i18next";
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalData } from '../../../../../contexts/GlobalDataProvider';
import query from '../../../../../server/constants.api';
import useFetch from "../../../../../server/useFetch";
import { PageContent, PageSection, PageTitle } from "../../../../../styles/global/Pages.styled";
import { handleNotificationWithAction } from '../../../../../utils/config/notificationHandler';
import Spinner from '../../../Atomics/Spinner';
import { Text } from '../../../Atomics/Text';
import permissionToEdit from '../AccessUtils';
import { getCustomizedButtonsForConfiguration } from '../ConfigurationUtils';
import ItemCreateEdit from './ItemCreateEdit/ItemCreateEdit';
import ItemDetailModal from './ItemDetailModal/ItemDetailModal';
import ItemRelations from './ItemRelations/ItemRelations';
import { filtersTypes, getFamilyTypeFilterForApi, transformItemData } from './ItemsConfig/ItemsConfig';
import ItemsFilter from './ItemsFilter/ItemsFilter';

export const ItemsConfigurations = ({ ...props }) => {

    // Obtém a função de tradução do hook useTranslation para uso na tradução de textos
    const { t } = useTranslation();

    const tableConfiguration = {
        collumnsConfiguration: [
            {
                title: t('Name'),
                dataIndex: 'name',
            },
            {
                title: t('Type'),
                dataIndex: 'type',
            },
            {
                title: t('Family'),
                dataIndex: 'family',
            },
            {
                title: t('Class'),
                dataIndex: 'class',
            },
            {
                title: t('Layer'),
                dataIndex: 'layer',
            },
        ]
    };

    const { subTenants, isFetchingSubTenants } = useGlobalData();

    const [itemsForTable, setItemsForTable] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [itemSelected, setItemSelected] = useState({ item: null, action: null });
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [filteredDistinctProperties, setFilteredDistinctProperties] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState({ selected: null, value: null });
    const [hasFetchedInitialItemsData, setHasFetchedInitialItemsData] = useState(false);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isItemRelationsModalOpen, setIsItemRelationsModalOpen] = useState(false);
    const allowedToWrite = permissionToEdit(['Configurations', 'ItemsConfigurations'])

    const {
        data: itemsData,
        isFetching: isFetchingItemsData,
        refetch: refetchItemsData,
        remove: removeItemsData
    } = useFetch('/items',
        'getItemsDataForItemsConfig',
        {
            tenant: selectedTenant,
            ...getFamilyTypeFilterForApi(selectedFilter)
        },
        false,
        null, query.middlewareURL);

    //------Item------
    const {
        data: itemProperties,
        isFetching: isFetchingItemProperties,
        refetch: refetchItemProperties,
        remove: removeItemProperties
    } = useFetch('/items/properties',
        'getItemProperties',
        {
            "full-properties": true,
            family: itemSelected?.item?.family,
            class: itemSelected?.item?.class,
        },
        false,
        null,
        query.middlewareURL);

    const {
        data: itemData,
        isFetching: isFetchingItemData,
        refetch: refetchItemData,
        remove: removeItemData
    } = useFetch(`/items/id/${itemSelected?.item?.id}/raw`,
        'getItemData',
        null,
        false,
        null,
        query.middlewareURL);

    const {
        isFetching: isFetchingRemoveItemData,
        refetch: refetchRemoveItemData,
        isSuccess: isRemoveItemSuccessful,
        isError: isRemoveItemError,
        error: removeItemError,
        remove: removeItemFetch,
    } = useFetch(
        `/items/id/${itemSelected?.item?.id}`,
        'removeItem',
        null,
        false,
        'DELETE',
        query.middlewareURL
    )

    //------Filters------
    const {
        data: distinctProperties,
        refetch: refetchDistinctProperties,
        isFetching: isFetchingDistinctProperties,
        remove: removeDistinctProperties
    } = useFetch('/items/distinct-properties',
        'getDistinctPropertiesForItems',
        {
            tenant: selectedTenant,
            properties: "family,type",
        },
        false,
        null,
        query.middlewareURL
    )

    /**
     * Set the selected tenant to the first tenant in the subTenants array when the component mounts
     */
    useEffect(() => {
        if (subTenants && subTenants.length > 0) {
            setSelectedTenant(subTenants[0]);
        }
    }, [subTenants])

    /**
     * Used to remove empty values from the distinct properties
     * Updates the selected filter with the first value of the filtered distinct properties
     */
    useEffect(() => {
        if (distinctProperties && !isFetchingDistinctProperties) {
            const filteredResult = Object.keys(distinctProperties).reduce((result, key) => {
                result[key] = distinctProperties[key].filter(item => item !== "");
                return result;
            }, {});

            setFilteredDistinctProperties(filteredResult);

            setSelectedFilter({ selected: filtersTypes.family, value: filteredResult.family[0] });
        }
    }, [distinctProperties, isFetchingDistinctProperties]);

    /**
     * Refetch the distinct properties when a new tenant is selected
     */
    useEffect(() => {
        if (selectedTenant) {
            refetchDistinctProperties();
        }
    }, [selectedTenant]);

    /**
     * Make the first fetch of the items data when the family filter is selected
     */
    useEffect(() => {
        if (selectedFilter.selected && !hasFetchedInitialItemsData) {
            refetchItemsData();
            setHasFetchedInitialItemsData(true);
        }
    }, [selectedFilter.selected, hasFetchedInitialItemsData]);

    /**
     * Refetch the item data and item properties when a new item is selected
     */
    useEffect(() => {
        if (itemSelected?.item) {
            switch (itemSelected?.action) {
                case 'view':
                case 'edit':
                    refetchItemData();
                    refetchItemProperties();
                    break;
                case 'delete':
                    refetchRemoveItemData();
                    break;
            }
        } else {
            removeItemProperties();
            removeItemData();
            removeItemFetch();
        }


    }, [itemSelected]);

    /**
     * Show notification when an error occurs while removing an item
     * Or when the item is removed successfully
     */
    useEffect(() => {
        const successMessage = 'Remove Item Success';
        const errorMessage = 'Remove Item Error';

        handleNotificationWithAction(isFetchingRemoveItemData, isRemoveItemSuccessful, isRemoveItemError, removeItemError,
            successMessage, errorMessage, t, onRemoveItemSuccess, () => { setItemSelected({ item: null, action: null }) });

    }, [isRemoveItemError, isRemoveItemSuccessful, isFetchingRemoveItemData]);

    /**
     * Remove the items data and reset the hasFetchedInitialItemsData state when the component is unmounted
     * Also remove the filters data
     */
    useEffect(() => {
        return () => {
            setHasFetchedInitialItemsData(false);
            removeItemsData();

            removeFiltersData();
        }
    }, []);

    useEffect(() => {
        if (itemsData) {
            setItemsForTable(itemsData);
        }
    }, [itemsData]);

    /**
     * Function to be called when the item is removed successfully
     */
    const onRemoveItemSuccess = () => {
        setItemsForTable(itemsForTable.filter(item => item.id !== itemSelected.item.id));
    }

    /**
     * Removes filters data by calling removeDistinctProperties function.
     */
    const removeFiltersData = () => {
        removeDistinctProperties();
    }

    return (
        <PageContent className={clsx('building_room_details', props?.className)}>
            <ItemDetailModal
                isModalVisible={isModalVisible}
                handleClose={() => {
                    setItemSelected({ item: null, action: null });
                    setIsModalVisible(false);
                }}
                item={transformItemData(itemData, itemProperties)}
                isFetching={isFetchingItemData || isFetchingItemProperties}
            />
            <ItemRelations
                handleItemRelationsClose={() => {
                    setItemSelected({ item: null, action: null });
                    setIsItemRelationsModalOpen(false)
                }}
                isItemRelationsModalOpen={isItemRelationsModalOpen}
                itemSelected={itemSelected.item}
            />
            <ItemCreateEdit
                isCreateModalOpen={isCreateModalOpen}
                isEditModalOpen={isEditModalOpen}
                onCreateModalClose={() => {
                    setIsCreateModalOpen(false);
                }}
                onCreationSuccess={() => {
                    refetchItemsData();
                }}
                onEditModalClose={() => {
                    setItemSelected({ item: null, action: null });
                    setIsEditModalOpen(false);
                }}
                itemToEdit={transformItemData(itemData, itemProperties, true)}
            />
            <PageSection>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <ItemsFilter
                            tenantOptions={subTenants || []}
                            selectedTenant={selectedTenant}
                            setSelectedTenant={setSelectedTenant}
                            familyOptions={filteredDistinctProperties?.family}
                            typeOptions={filteredDistinctProperties?.type}
                            selectedFilter={selectedFilter}
                            setSelectedFilter={setSelectedFilter}
                            isTenantFilterDisabled={isFetchingSubTenants || !selectedTenant}
                            isFilterDisabled={isFetchingSubTenants || isFetchingDistinctProperties
                                || !selectedTenant || !filteredDistinctProperties?.family || !filteredDistinctProperties?.type}
                            onApplyFilters={refetchItemsData}
                            isApplyButtonDisabled={isFetchingItemsData || isFetchingDistinctProperties || isFetchingSubTenants
                                || !selectedTenant || !selectedFilter?.value}
                        />
                    </Col>
                    <Col span={24}>
                        <PageTitle>
                            <Text $fontColor='coolgray-12' $fontSize='text_md' $weight='medium' $marginRight='s03'>{t('Items List')}</Text>
                        </PageTitle>
                    </Col>
                    <Col span={24}>
                        {itemsData && !isFetchingItemsData ?
                            <ComplexTable
                                fetchData={itemsForTable}
                                tableConfiguration={tableConfiguration}
                                language={i18next.resolvedLanguage}
                                tableId='itemsTable'
                                detailsButton={true}
                                handleDetails={(item) => {
                                    setItemSelected({ item: item, action: 'view' });
                                    setIsModalVisible(true);
                                }}
                                graphButton={allowedToWrite}
                                handleGraph={(item) => {
                                    setItemSelected({ item: item, action: 'relations' });
                                    setIsItemRelationsModalOpen(true);
                                }}
                                pagination={{ pageSize: 15 }}
                                fixedActions={true}
                                deleteButton={allowedToWrite}
                                handleDelete={(item) => {
                                    setItemSelected({ item: item, action: 'delete' });
                                }}
                                editButton={allowedToWrite}
                                handleEdit={(item) => {
                                    setItemSelected({ item: item, action: 'edit' });
                                    setIsEditModalOpen(true);
                                }}
                                customizedButtons={
                                    getCustomizedButtonsForConfiguration(allowedToWrite, () => { setIsCreateModalOpen(true) }, t)
                                }
                                editButtonTooltipMessage={t('Edit Item')}
                                deleteButtonTooltipMessage={t('Delete Item')}
                                detailsButtonTooltipMessage={t('Item Details')}
                                graphButtonTooltipMessage={t('Item Relations')}
                            />
                            :
                            <div style={{ height: 'inherit', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <Spinner stroke="green" />
                            </div>
                        }
                    </Col>
                </Row>
            </PageSection>
        </PageContent>
    )
}
